import React, { Dispatch, useEffect, useState } from 'react';
import { Tabs } from '@landr/maestro';
import { Edges, PageContextType } from 'types';
import {
    getUserCountry,
    getCountryCurrency,
    DEFAULT_COUNTRY_CURRENCY,
    CountryCurrency,
    isLocationContainsUID,
    getTwoLetterLanguageCode,
} from 'helpers';
import { SliceContext } from 'components/Slices/types';
import { navigate } from 'gatsby';
import PricingContext, { PricingContextModel, pricingContextDefaultValues } from '../common/PricingContext';
import { PricingTabDocument, CommonPricingTranslations } from '../types';
import { isLocationContainsTabsUID, temporaryHandleV2Tabs } from '../common/helpers';
import { PricingTab } from './PricingTab';
import { SessionsPricingTab } from './SessionsPricingTab';

const updatePath = (nextTab: string, pageContext: PageContextType) => {
    const languageCode = getTwoLetterLanguageCode(pageContext.lang);
    navigate(`${languageCode}/${pageContext.uid}/${nextTab}`);
};

export type Tabs = Array<{
    tab: PricingTabDocument;
}>;

export type PricingTabsProps = {
    pricingTabs: Tabs;
    pricingTabsData: Edges<PricingTabDocument>;
    translations: CommonPricingTranslations;
    sliceContext: SliceContext;
    pageContext: PageContextType;
    location: Location;
};

export const PricingTabs: React.FC<PricingTabsProps> = ({
    pricingTabs,
    pricingTabsData,
    translations,
    sliceContext,
    pageContext,
    location,
}) => {
    const [pricingContext, setPricingContext]: [PricingContextModel, Dispatch<PricingContextModel>] = useState(
        pricingContextDefaultValues,
    );
    const [countryCurrency, setCountryCurrency] = useState<CountryCurrency | null>(null);
    const [selectedTab, setSelectedTab] = useState<string>();
    const languageCode = getTwoLetterLanguageCode(pageContext.lang);

    useEffect(() => {
        // getUserCountry() doesn't implement a "reject" method since it passes undefined if no geolocation was found
        getUserCountry().subscribe((countryCode: string) => {
            /*
                This code will always be executed even if no geolocation is found in getUserCountry,
                so isCountryCurrencyLoaded will always be set to true after the geolocation succeed or fails,
                hence the pricing will always be shown, with the default currency (USD) as fallback.
            */
            if (countryCode) {
                setCountryCurrency(getCountryCurrency(countryCode));
            } else {
                setCountryCurrency(DEFAULT_COUNTRY_CURRENCY);
            }
        });
    }, []);

    useEffect(() => {
        setPricingContext({
            pricingTabs,
            pricingTabsData,
            translations,
            countryCurrency: countryCurrency as CountryCurrency,
            sliceContext,
        });
    }, [countryCurrency, pageContext.lang]);

    useEffect(() => {
        if (location) {
            const URLContainsTabUID = isLocationContainsTabsUID(location, pricingTabs);
            if (URLContainsTabUID) {
                const pricingTabSelectedFromUrl = pricingTabs.find(({ tab }) => {
                    // TODO remove this when we can deprecate the old pricing tabs in prismic
                    const tabUid = temporaryHandleV2Tabs(tab._meta.uid);
                    return isLocationContainsUID(location, tabUid);
                });
                pricingTabSelectedFromUrl &&
                    setSelectedTab(temporaryHandleV2Tabs(pricingTabSelectedFromUrl.tab._meta.uid));
            } else {
                // set the default selected tab
                setSelectedTab(temporaryHandleV2Tabs(pricingTabs[0].tab._meta.uid));
            }
        }
    }, [location]);

    const SessionsTabUid = 'sessions';

    return (
        <PricingContext.Provider value={pricingContext}>
            <Tabs selectedTab={selectedTab} onSelectTab={(nextTab: string) => updatePath(nextTab, pageContext)}>
                {pricingTabs.map(({ tab }) => {
                    return (
                        tab.name &&
                        tab._meta && (
                            <Tabs.Pane
                                // TODO remove this when we can deprecate the old pricing tabs in prismic
                                key={temporaryHandleV2Tabs(tab._meta.uid)}
                                aria-label={tab.name}
                                label={tab.name}
                                // TODO remove this when we can deprecate the old pricing tabs in prismic
                                name={temporaryHandleV2Tabs(tab._meta.uid)}
                            >
                                <>
                                    {tab._meta.uid !== SessionsTabUid && (
                                        <PricingTab
                                            tab={tab as PricingTabDocument}
                                            languageCode={languageCode}
                                        ></PricingTab>
                                    )}
                                    {tab._meta.uid === SessionsTabUid && (
                                        <SessionsPricingTab
                                            tab={tab as PricingTabDocument}
                                            languageCode={languageCode}
                                        ></SessionsPricingTab>
                                    )}
                                </>
                            </Tabs.Pane>
                        )
                    );
                })}
            </Tabs>
        </PricingContext.Provider>
    );
};
